import { useCallback } from 'react';

import {
  type FetchStatus,
  type UseQueryResult,
  queryOptions,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { StoreApi, useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { PeerTypes } from '../../types/peers';
import {
  PeerParticipantResponse,
  PeerResponse,
  PeerSearch,
  PeerTeamResponse,
  PeersResponse,
} from './types';
import { campaignPeerWithType } from './utils';

const useCurrentCauseQueryString = (causeId?: string) => {
  return `cause_id=${causeId}`;
};

// Return either a participant, or team based on the peerType
const useShowCampaignPeer = <T extends PeerTypes>({
  causeId,
  campaignId,
  peerId,
  peerType,
  queryOptions,
}: {
  causeId?: string;
  campaignId?: string;
  peerId?: string;
  peerType: T;
  queryOptions?: PeerSearch;
}) => {
  const StoreApi = useStoreApi();
  const causeQuery = useCurrentCauseQueryString(causeId);
  const { queryString, queryObject } = createQueryOptions<PeerSearch>({
    search: queryOptions,
    existingSearchString: causeQuery,
  });

  const getParticipant = async () => {
    const { data } = await StoreApi.get<PeerResponse<T>>(
      `/store_items/${campaignId}/${peerType}/${peerId}?${queryString}`
    );
    const peerData = data[
      peerType == 'participants' ? 'participant' : 'team'
    ] as T extends 'participants'
      ? PeerParticipantResponse['participant']
      : PeerTeamResponse['team'];
    return campaignPeerWithType(peerData);
  };

  const isEnabled = !!causeId && !!campaignId && !!peerId && !!peerType;

  const key = isEnabled
    ? [
        ...storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].id(peerId).detail,
        queryObject,
      ]
    : [];
  return useQuery({
    queryKey: key,
    queryFn: getParticipant,
    enabled: isEnabled,
  });
};

type GetCampaignsContext = {
  meta?: {
    campaignId?: string;
    peer_type?: PeerTypes;
    queryString?: string;
    causeId?: string;
  };
};

const useGetPeers = <T extends PeerTypes | undefined>() => {
  const queryClient = useQueryClient();
  const StoreApi = useStoreApi();
  const getPeers = async (context: GetCampaignsContext) => {
    const { campaignId, peer_type, queryString, causeId } = context.meta || {};
    const { data } = await StoreApi.get<PeersResponse<T>>(
      `store_items/${campaignId}/${peer_type}?${queryString}`
    );

    const peers = data?.[peer_type == 'participants' ? 'participants' : 'teams'];

    peers?.forEach((peer) => {
      const isEnabled = !!causeId && !!campaignId && !!peer.uuid && !!peer_type;
      if (!isEnabled) return;
      queryClient.setQueryData(
        storeKeys.id(causeId).items.campaigns.id(campaignId)[peer_type].id(peer.uuid).detail,
        peer
      );
    });

    return data;
  };
  return getPeers;
};

const useListCampaignPeers = <T extends PeerTypes, U = PeersResponse<T>>({
  pagination,
  queryOptions,
  peer_type,
  select,
  causeId,
  campaignId,
}: {
  pagination?: QueryPagination;
  queryOptions?: PeerSearch;
  peer_type: T;
  select?: (data?: PeersResponse<T>) => U;
  causeId?: string;
  campaignId?: string;
}) => {
  const causeQuery = useCurrentCauseQueryString(causeId);

  const getCampaignPeers = useGetPeers<T>();
  const { queryString, queryObject, queryPagination } = createQueryOptions<PeerSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });
  const peerKey = peer_type == 'participants' ? 'participants' : 'teams';

  const key =
    campaignId && causeId && peer_type
      ? [
          ...storeKeys.id(causeId).items.campaigns.id(campaignId)[peerKey].all,
          queryObject,
          queryPagination,
        ]
      : [];
  return useQuery({
    queryKey: key,
    queryFn: getCampaignPeers,
    enabled: !!campaignId && !!causeId && !!peer_type,
    meta: { campaignId, peer_type, queryString, causeId },
    // placeholderData: keepPreviousData,
    select,
  });
};

// This uses a separate pattern where we create the query Options upfront so they could be reused for other queries.
// This intentionally duplicates the logic of useListCampaignPeers but is used for combined queries where we need to pass the query options to the query.
// If we decide to utilize this pattern- we would replace the useListCombinedPeerQueries hook with this one.
// -db 2025-12-22
const listCampaignPeersQuery = <T extends PeerTypes>({
  pagination,
  queryOptions: searchOptions,
  peer_type,
  causeId,
  campaignId,
  enabled = true,
}: {
  pagination?: QueryPagination;
  queryOptions?: PeerSearch;
  peer_type: T;
  causeId?: string;
  campaignId?: string;
  enabled?: boolean;
}) => {
  const { queryString, queryObject, queryPagination } = createQueryOptions<PeerSearch>({
    search: searchOptions,
    pagination,
    existingSearchString: `cause_id=${causeId}`,
  });

  const getPeers = async () => {
    const { data } = await StoreApi.get<PeersResponse<T>>(
      `store_items/${campaignId}/${peer_type}?${queryString}`
    );
    return data;
  };

  const peerKey = peer_type == 'participants' ? 'participants' : 'teams';

  const key =
    campaignId && causeId && peer_type
      ? [
          ...storeKeys.id(causeId).items.campaigns.id(campaignId)[peerKey].all,
          queryObject,
          queryPagination,
        ]
      : [];

  return queryOptions({
    queryKey: key,
    queryFn: getPeers,
    enabled: !!campaignId && !!causeId && !!peer_type && enabled,
  });
};

type PeerQueryOptions = {
  enabled?: boolean;
  queryOptions?: PeerSearch;
};

// returns promises for each participant type  and use a promise.all to wait for all of them to resolve
const useListCombinedPeerQueries = ({
  causeId,
  campaignId,
  participantsQueryOptions,
  teamsQueryOptions,
}: {
  causeId?: string;
  campaignId?: string;
  participantsQueryOptions?: PeerQueryOptions;
  teamsQueryOptions?: PeerQueryOptions;
}) => {
  return useQueries({
    queries: [
      listCampaignPeersQuery({
        peer_type: 'participants',
        causeId,
        campaignId,
        ...participantsQueryOptions,
      }),
      listCampaignPeersQuery({
        peer_type: 'teams',
        causeId,
        campaignId,
        ...teamsQueryOptions,
      }),
    ],
    combine: useCallback(
      (
        results: readonly [
          UseQueryResult<PeersResponse<'participants'>, Error>,
          UseQueryResult<PeersResponse<'teams'>, Error>,
        ]
      ) => ({
        data: {
          participantsData: results[0].data,
          teamsData: results[1].data,
        },
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        isPending: results.some((result) => result.isPending),
        fetchStatus: results.some((result) => result.fetchStatus === 'fetching')
          ? ('fetching' as FetchStatus)
          : ('idle' as FetchStatus),
        isSuccess: results.every((result) => result.isSuccess),
      }),
      []
    ),
  });
};

export {
  listCampaignPeersQuery,
  useGetPeers,
  useListCampaignPeers,
  useListCombinedPeerQueries,
  useShowCampaignPeer,
};
